import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Style from './Governance.module.css';
import DataControls from './DataControls';
import WebControls from './WebControls';
import AIControls from './AIControls';
import ExtensionControls from './ExtensionControls';
import LoaderWrapper from '../common/LoaderWrapper/LoaderWrapper';
import {
  CONTROL_META, TABS, STATUS_BADGE, VersionBadge, controlTypeFromSearch, withType, editorPath,
} from './GovernanceShared';
import {
  getGovernancePolicy,
  getGovernanceDraft,
  listGovernanceSites,
  deleteGovernanceSite,
} from '../../store/entities/governance';

// Governance landing.
//   DATA  → read-only view of the PUBLISHED policy (rule tables) + an editor entry
//           button. Unchanged from the original delivery.
//   WEB/AI → the SITE LIST ("policy = site"). Each site is a row with its own
//           version state; Edit/Add/Delete act here (no top-level editor button,
//           no policy-level version badge). Gated on Data Controls being enabled —
//           if the customer has no published DATA policy, a gate replaces the list.
const Governance = (props) => {
  const {
    policy, draft, policyLoading, policyType, draftType,
    sites, sitesType, sitesLoading,
  } = props;
  const customerId = props.match?.params?.id;
  const type = controlTypeFromSearch(props.location?.search);
  const meta = CONTROL_META[type] || CONTROL_META.DATA;
  const isApp = type === 'WEB' || type === 'AI';
  // EXTENSION is a THIRD model, not a site model: a list of per-extension policies
  // whose single rule is ATTACHED from a seeded catalogue rather than authored, with
  // no apps, no email domains and no copied DATA rule set. It is therefore
  // deliberately NOT folded into isApp -- and it needs no Data-Controls-enabled gate
  // either, since it does not reuse the DATA rule set at all.
  const isExtension = type === 'EXTENSION';

  // DATA/WEB/AI share one governance slice. Only surface the slice data whose
  // tagged control type matches the active tab, so switching tabs (or an
  // out-of-order response after rapid switching) never renders the previous
  // type's data. Untagged data (null — pristine state / tests) matches.
  const forType = (data, dataType, want) => (dataType == null || dataType === want ? data : null);
  const activePolicy = forType(policy, policyType, type);
  const activeDraft = forType(draft, draftType, type);
  const activeSites = forType(sites, sitesType, type) || [];

  // ── WEB/AI gate: Data Controls is "enabled" ⇔ a published DATA policy exists.
  // The landing fetches the DATA policy (into the shared slice, tagged DATA) purely
  // to answer this; the site list itself comes from `sites`.
  const dataPolicy = forType(policy, policyType, 'DATA');
  const dataEnabled = !!(dataPolicy && dataPolicy.version);

  // ── DATA header derivations (unchanged) ──
  const hasOpenDraft = !!(activeDraft && activeDraft.version);
  const draftStatus = hasOpenDraft
    ? (activeDraft.version.StatusCode || activeDraft.version.statusCode || null)
    : null;
  const publishedVersion = activePolicy && activePolicy.version
    ? (activePolicy.version.VersionNumber ?? activePolicy.version.versionNumber)
    : null;
  const draftVersionNumber = hasOpenDraft
    ? (activeDraft.version.VersionNumber ?? activeDraft.version.versionNumber)
    : null;

  useEffect(() => {
    if (!customerId) return;
    if (isExtension) {
      // Nothing to fetch here: ExtensionControls owns its own reads (policies,
      // predefined rules, lookup, aggregate).
      //
      // Falling through to the else-branch would be actively wrong, not just
      // wasteful: getGovernancePolicy would store an ARRAY of extension policies in
      // the single-policy slot (that endpoint returns a list, not one object), and
      // getGovernanceDraft would call GET /governance/extension/draft, which the API
      // deliberately does not serve -- an extension policy's draft state is already
      // carried on each row of the list. The rejection would surface as an error
      // banner on a tab that had loaded perfectly well.
      return;
    }
    if (isApp) {
      // Sites for the active tab + the DATA policy (for the enabled-gate).
      props.listGovernanceSites(customerId, type);
      props.getGovernancePolicy(customerId, 'DATA');
    } else {
      props.getGovernancePolicy(customerId, type);
      props.getGovernanceDraft(customerId, type);
    }
  }, [customerId, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToTab = (key) => {
    if (!customerId) return;
    props.history.push(withType(`/customers/${customerId}/governance`, key));
  };
  const goToEditor = () => {
    if (!customerId) return;
    props.history.push(withType(`/customers/${customerId}/governance/edit`, type));
  };

  // ── WEB/AI site actions ──
  // logicalExtensionId is passed ONLY by Extension Controls' per-row Attach (EXTENSION type), so the
  // editor can open with that extension + its predefined rule pre-selected. WEB/AI callers and the
  // generic footer Attach pass nothing, so it's a no-op for them (editor opens with an empty picker).
  const onAddSite = (logicalExtensionId) => customerId
    && props.history.push(editorPath(customerId, type, null, undefined, logicalExtensionId));
  const onEditSite = (site) => customerId && site && props.history.push(editorPath(customerId, type, site.policyId));
  const onDeleteSite = async (site) => {
    if (!customerId || !site) return;
    const res = await props.deleteGovernanceSite(customerId, type, site.policyId);
    if (res !== undefined) props.listGovernanceSites(customerId, type);
  };

  // The DATA landing always reflects the live PUBLISHED policy — never an
  // in-progress draft (surfaced only as a status badge in the header).
  const displayRules = activePolicy && Array.isArray(activePolicy.rules) ? activePolicy.rules : [];

  // ── WEB/AI body: gate → site list ──
  const renderAppBody = () => {
    if (sitesLoading || policyLoading) return null; // LoaderWrapper shows the spinner
    if (!dataEnabled) {
      return (
        <div className={Style.gateCard}>
          <div className={Style.gateTitle}>Enable Data Controls first</div>
          <p className={Style.gateText}>
            {meta.title} reuse your Data Controls rule set — each site or app carries its own copy of the
            published rules. Publish a Data Controls policy before adding {type === 'AI' ? 'AI apps' : 'sites'} here.
          </p>
          <button
            className={`${Style.btn} ${Style.btnPrimary}`}
            onClick={() => props.history.push(withType(`/customers/${customerId}/governance`, 'DATA'))}
          >
            Go to Data Controls
          </button>
        </div>
      );
    }
    const Body = type === 'AI' ? AIControls : WebControls;
    return (
      <Body
        controlType={type}
        customerId={customerId}
        sites={activeSites}
        onAddSite={onAddSite}
        onEditSite={onEditSite}
        onDeleteSite={onDeleteSite}
      />
    );
  };

  return (
    <div className={Style.page}>
      {/* ── Fixed header: tabs + title (+ DATA-only published version + editor entry) ── */}
      <div className={Style.gvHeader}>
        <div className={Style.tabBar}>
          {TABS.map((t) => {
            const active = t.enabled && t.key === type;
            return (
              <button
                key={t.key}
                className={`${Style.tabBtn} ${active ? Style.tabBtnActive : ''} ${!t.enabled ? Style.tabBtnDisabled : ''}`}
                disabled={!t.enabled}
                title={t.enabled ? '' : 'Coming soon'}
                onClick={t.enabled ? () => goToTab(t.key) : undefined}
              >
                {t.label}
                {!t.enabled && <span className={Style.comingSoon}>Soon</span>}
              </button>
            );
          })}
        </div>

        {(isApp || isExtension) ? (
          // WEB/AI: title + subtitle only — no policy-level version badge and no
          // top editor button (Edit/Add/Delete live on the site rows below).
          <div className={Style.pageHeader}>
            <div>
              <h1 className={Style.pageTitle}>{meta.title}</h1>
              <p className={Style.pageSubtitle}>{meta.subtitle}</p>
            </div>
          </div>
        ) : (
          <div className={Style.pageHeader}>
            <div>
              <h1 className={Style.pageTitle}>
                {meta.title}
                {publishedVersion
                  ? <VersionBadge statusCode="PUBLISHED" versionNumber={publishedVersion} />
                  : <span className={`${Style.badge} ${Style.neutral}`} style={{ marginLeft: 10 }}>No policy published</span>}
                {hasOpenDraft && (draftStatus === 'DRAFT' || draftStatus === 'APPROVED') && (
                  <span className={Style.draftStateInline}>
                    ({(STATUS_BADGE[draftStatus] || {}).label || draftStatus} v{draftVersionNumber})
                  </span>
                )}
              </h1>
              <p className={Style.pageSubtitle}>{meta.subtitle}</p>
            </div>
            <button className={`${Style.btn} ${Style.btnPrimary}`} onClick={goToEditor}>
              {publishedVersion ? meta.editLabel : meta.createLabel}
            </button>
          </div>
        )}
      </div>

      {/* ── Scrollable body ── */}
      <div className={Style.gvScrollBody}>
        {isExtension ? (
          // Edit routes to the per-extension editor by policyId, the same
          // editorPath() contract Web/AI sites use — the whole draft → approve →
          // publish lifecycle lives there, not on this landing table.
          <ExtensionControls customerId={customerId} onEditRule={onEditSite} onAddRule={onAddSite} />
        ) : isApp ? (
          <LoaderWrapper loading={sitesLoading || policyLoading} waitForData={false} styles={{ minHeight: '40px' }} isFlex={true}>
            {renderAppBody()}
          </LoaderWrapper>
        ) : (
          <LoaderWrapper loading={policyLoading} waitForData={false} styles={{ minHeight: '40px' }} isFlex={true}>
            <DataControls rules={displayRules} />
          </LoaderWrapper>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  policy: state.entities.governance.policy,
  draft: state.entities.governance.draft,
  policyType: state.entities.governance.policyType,
  draftType: state.entities.governance.draftType,
  policyLoading: state.entities.governance.policyLoading,
  sites: state.entities.governance.sites,
  sitesType: state.entities.governance.sitesType,
  sitesLoading: state.entities.governance.sitesLoading,
});

const mapDispatchToProps = (dispatch) => ({
  getGovernancePolicy: (customerId, controlType) => Promise.resolve(dispatch(getGovernancePolicy(customerId, controlType))),
  getGovernanceDraft: (customerId, controlType) => Promise.resolve(dispatch(getGovernanceDraft(customerId, controlType))),
  listGovernanceSites: (customerId, controlType) => Promise.resolve(dispatch(listGovernanceSites(customerId, controlType))),
  deleteGovernanceSite: (customerId, controlType, policyId) => Promise.resolve(dispatch(deleteGovernanceSite(customerId, controlType, policyId))),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Governance));
